/**
 * @file authService.js
 * @description APIs for authentication requests.
 **/

import { isPWA } from '../utils/deviceCheck';
import {
    authHttp,
    authHttpWithAuthorization,
    userApiHttp,
} from './httpService';
import { v4 as uuidv4 } from 'uuid';

/**
 * If MFA is enabled, it will return the necessary MFA challenge information.
 *
 * @param {string} username - The username for authentication.
 * @param {string} password - The password for authentication.
 * @param {string} companyCode - The company code for authentication.
 * @param {string} protocol - The protocol used to send the MFA code.
 * @param {string} code - The MFA code to be verified.
 * @param {bool} rememberMe - Tells server to bypass MFA for forseeable future for this user.
 * @returns {Promise<Object>} - The response data or error object.
 * @throws {Error} - If there is an error requesting the token.
 */
export const requestToken = async (
    username,
    password,
    companyCode,
    rememberMe = false,
    protocol = null,
    code = null,
) => {
    const headers = {
        Authorization: `Basic ${btoa(`${username}:${password}`)}`,
        ...(companyCode ? { 'X-Tenant-Namespace': companyCode } : {}),
    };

    try {
        const isMobilePWA = await isPWA();
        const deviceType = isMobilePWA ? 'Mobile' : 'Web';

        let deviceID = localStorage.getItem('deviceID');
        if (!deviceID) {
            deviceID = uuidv4();
            localStorage.setItem('deviceID', deviceID);
        }

        const response = await authHttp.post(
            '/v2/auth/user/token',
            {
                device_id: deviceID,
                platform: deviceType,
                protocol: protocol,
                code: code,
                remember_me: rememberMe,
            },
            { headers, withCredentials: true },
        );
        return response.data;
    } catch (error) {
        if (
            error.response &&
            error.response.status === 400 &&
            error.response.data.error.code === 'MFA_ENABLED'
        ) {
            return error.response.data;
        } else {
            console.error('Error requesting token:', error);
            throw error;
        }
    }
};

/**
 * Sends an MFA code to the user's email.
 *
 * @param {string} username - The username of the user.
 * @param {string} password - The password of the user.
 * @param {string} protocol - The protocol to use for sending MFA code, e.g., "email".
 * @param {string} companyCode - The company code for authentication.
 * @returns {Promise<object>} A promise that resolves to the MFA sending info.
 */
export const sendMfaCode = async (
    username,
    password,
    protocol,
    destination = 'primary_email', // "primary_phone", "optional_email", "optional_phone"
    companyCode = null,
) => {
    const headers = {
        Authorization: `Basic ${btoa(`${username}:${password}`)}`,
        ...(companyCode ? { 'X-Tenant-Namespace': companyCode } : {}),
    };

    try {
        let device_id = localStorage.getItem('deviceID');
        if (!device_id) {
            device_id = uuidv4();
            localStorage.setItem('deviceID', device_id);
        }

        const response = await authHttp.post(
            '/v2/auth/user/mfa/send',
            { protocol, destination, device_id },
            { headers },
        );
        return response.data;
    } catch (error) {
        console.error('Error sending MFA code:', error);
        throw error;
    }
};

/**
 * Retrieves the configuration data for the specified namespace.
 *
 * @param {string} namespace - The namespace for the configuration data.
 * X-Tenant-Namespace should not be passed when calling this API to avoid CORS errors.
 * @returns {Promise<object | null>} A promise that resolves to the configuration data.
 */
export const getConfigData = async namespace => {
    try {
        const response = await userApiHttp.get(
            `/v2/user/public/config?namespace=${namespace}`,
            { skipTenantHeader: true },
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving configuration data:', error);
        return null;
    }
};

/**
 * Sends a forgot password request for a user.
 *
 * @param {string} username - The email address or phone of the user.
 * @returns {Promise<void>} - A promise that resolves when the email is sent.
 * @throws {Error} - If there is an error with the forgot password request.
 */
export const sendForgotPasswordEmail = async (
    tenantNamespace,
    username = null,
) => {
    try {
        const response = await authHttp.post('/v2/auth/user/forgot', {
            tenant_namespace:
                tenantNamespace ??
                window.localStorage.getItem('companyCode') ??
                null,
            username: username,
        });
        return response.data;
    } catch (error) {
        console.error('Error sending forgot password email:', error);
        throw error;
    }
};

/**
 * Resets the user's password using the password reset token.
 *
 * @param {string} resetToken - The token received by the user for resetting the password.
 * @param {string} newPassword - The new password that the user wants to set.
 * @returns {Promise<Object>} - The response data containing the username.
 * @throws {Error} - If there is an error with the reset password request.
 */
export const resetPassword = async (resetToken, newPassword, verify) => {
    try {
        const response = await authHttp.post('/v2/auth/user/reset', {
            new_password: newPassword,
            token: resetToken,
            verify: verify,
        });
        return response.data;
    } catch (error) {
        console.error('Error resetting password:', error);
        throw error;
    }
};

/**
 * Refreshes the user's access token.
 *
 * @returns {Promise<object>} A promise that resolves to the new access token data.
 */
export const refreshAccessToken = async () => {
    try {
        const response = await authHttp.post(
            '/v2/auth/user/refresh',
            {},
            { withCredentials: true },
        );
        return response.data;
    } catch (error) {
        console.error('Error refreshing access token:', error);
        throw error;
    }
};

export const logoutSession = async () => {
    try {
        const response = await authHttp.post(
            '/v2/auth/user/logout',
            {},
            { withCredentials: true },
        );
        return response.data;
    } catch (error) {
        console.error('Error logging out:', error);
    }
};

export const impersonate = async impersonatedId => {
    try {
        const response = await authHttpWithAuthorization.post(
            `/v2/auth/user/impersonate?impersonatedId=${encodeURIComponent(impersonatedId)}`,
            { withCredentials: true },
        );
        return response.data;
    } catch (error) {
        console.error('Error impersonating user:', error);
        throw error;
    }
};
